import {Logger, User} from '@nh/angular-tses';
import {Reassignment} from 'app/core/services/role.service';
import {ROLE_METADATA} from 'app/permissions/role-metadata';
import {DepartureRole, DepartureRolesArray} from 'app/permissions/types';
import {toArray} from 'app/utils/array-utils';

export interface DeparturePermissions {
  id?: string; // set by the backend on persist
  departureId: string;
  createdBy?: string; // set by the backend on persist
  createdAt?: number; // set by the backend on persist
  approximateCreatedAt?: boolean;
  proposer?: string;
  designers?: string[];
  projectManager?: string;
  ssp?: string;
  technicalSpecialist?: string;
  authorisingSignatory?: string;
  additionalTechnicalReviewers?: string[];
  advisorySpecialists?: string[];
  assignee?: string;
  bulkReassignment?: boolean;
}

export function getAllAssignedUserIds(permissions: DeparturePermissions, rolesToGet: string[]): string[] {
  return [].concat(
    ...Object.keys(permissions || {})
      .filter((key: string) => (rolesToGet || []).includes(key) && permissions[key])
      .map((key: string) => toArray(permissions[key]))
  );
}

/**
 * Reassigns the roles of a given departure.
 *
 * @param reassignments The array of roles and users to be reassigned
 * @param departureId The id of the departure that the reassignments are being made against
 */
export function reassign(reassignments: Reassignment[], permissions: DeparturePermissions): DeparturePermissions {
  let newPermissions: DeparturePermissions = Object.assign({}, permissions);

  reassignments.forEach((reassignment: Reassignment) => {
    if (!Object.values(DepartureRole).includes(reassignment.role)) {
      Logger.error('role-error', `${reassignment.role} is not a valid departure role`);
    } else {
      let assignment: string | string[];

      if (reassignment.user instanceof User) {
        assignment = reassignment.user.id.value;
      } else if (reassignment.user instanceof Array) {
        assignment = reassignment.user.map((u: string | User) => {
          if (u instanceof User) {
            return u.id.value;
          } else {
            return u;
          }
        });
        if (!ROLE_METADATA[reassignment.role].multi) {
          assignment = assignment[0];
        }
      } else {
        assignment = reassignment.user;
      }

      if (ROLE_METADATA[reassignment.role].multi) {
        assignment = toArray(assignment);
      }

      newPermissions = Object.assign(newPermissions, {[reassignment.role]: assignment});
    }
  });

  // these will be set by the backend
  delete newPermissions.id;
  delete newPermissions.createdBy;
  delete newPermissions.createdAt;
  return newPermissions;
}

export function getRolesHeld(permissions: DeparturePermissions, userId: string): DepartureRole[] {
  if (!permissions || !userId) {
    return [];
  }

  const roles: DepartureRole[] = [];

  DepartureRolesArray.forEach((role: DepartureRole) => {
    const usersInRole: string[] = toArray(permissions[role]);
    if (usersInRole.includes(userId)) {
      roles.push(role);
    }
  });

  return roles;
}
